import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import { useRouter } from 'next/router';
import { useAccount } from '@frontastic-engbers/lib';
import { useFormat } from '@frontastic-engbers/helpers/hooks/useFormat';
import { Cart } from '@frontastic-engbers/types/cart/Cart';
import { VoucherInfo } from '@frontastic-engbers/types/engbers-custom';
import classNames from 'classnames';
import { Button, InputText } from '@engbers/components';
import styles from '../flyout-cart.module.scss';
import dynamic from 'next/dynamic';
const AnimatePresence = dynamic(() => import('framer-motion').then(module => module.AnimatePresence));
const CustomMotion = dynamic(() => import('@frontastic-engbers/lib/lib/custom-motion'));
interface ICartButtons {
  toCheckoutBtnLabel: string;
  voucherBtnLabel: string;
  toBasketBtnLabel: string;
  voucherPlaceholder: string;
  redeemVoucherBtnLabel: string;
  backBtnLabel: string;
  updateGlobalState: (isFlyoutCartOpen: boolean) => void;
  redeemDiscountCode: (code: string, pin: string) => Promise<Cart | string>;
  isEmptyCart: boolean;
  voucherRedeemedLabel: string;
  voucherInvalidLabel: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  pushDatalayer;
}
const slideFadeVariants = {
  hidden: {
    opacity: 0,
    y: '120%'
  },
  visible: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: '120%'
  }
};
export const FlyoutCartButtons: React.FC<ICartButtons> = ({
  toCheckoutBtnLabel,
  voucherBtnLabel,
  toBasketBtnLabel,
  voucherPlaceholder,
  redeemVoucherBtnLabel,
  backBtnLabel,
  updateGlobalState,
  redeemDiscountCode,
  isEmptyCart,
  voucherRedeemedLabel,
  voucherInvalidLabel,
  setLoading,
  pushDatalayer
}) => {
  const router = useRouter();
  const {
    formatMessage
  } = useFormat({
    name: 'cart'
  });
  const {
    loggedIn
  } = useAccount();
  const [isVoucherSectionOpen, setIsVoucherSectionOpen] = useState<boolean>(false);
  const [voucherValue, setVoucherValue] = useState<VoucherInfo>({
    voucherCode: '',
    voucherPin: ''
  });
  const [hasVoucherError, setHasVoucherError] = useState<boolean>(false);
  const [error, setError] = useState<string>(undefined);
  const [showHint, setShowHint] = useState<boolean>(false);
  const voucherCodeRef = useRef<HTMLInputElement>(null);
  const voucherBtnRef = useRef<HTMLButtonElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const shouldFocusVoucherBtn = useRef(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setVoucherValue({
      ...voucherValue,
      [e.target.name]: e.target.value.trim()
    });
  };
  const onApplyDiscount = (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    redeemDiscountCode(voucherValue.voucherCode, voucherValue.voucherPin).then(response => {
      const hasError = typeof response === 'string';
      setHasVoucherError(hasError);
      if (hasError) {
        setError(response);
      }
    }).catch(() => {
      setHasVoucherError(true);
    }).finally(() => {
      setLoading(false);
      setVoucherValue({
        voucherCode: '',
        voucherPin: ''
      });
      setShowHint(true);
    });
  };
  const closeVoucherInput = () => {
    setIsVoucherSectionOpen(false);
    setShowHint(false);
    shouldFocusVoucherBtn.current = true;
  };
  const closeCallback = () => {
    if (isVoucherSectionOpen) {
      setIsVoucherSectionOpen(hasVoucherError);
    }
  };
  const goToCheckout = () => {
    pushDatalayer('begin_checkout');
    router.push(loggedIn ? '/checkout' : '/checkout/login');
    updateGlobalState(false);
  };
  useEffect(() => {
    if (showHint) {
      hintRef.current?.focus();
    } else {
      voucherCodeRef.current?.focus();
    }
    const timeout = setTimeout(() => {
      setShowHint(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [showHint]);
  return <>
      <AnimatePresence exitBeforeEnter data-sentry-element="AnimatePresence" data-sentry-source-file="index.tsx">
        {isVoucherSectionOpen ? <CustomMotion id="voucher-section" className={styles.flyoutCartVoucherSection} key="voucherSection" initial="hidden" animate="visible" exit="exit" variants={slideFadeVariants} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} onAnimationComplete={() => {
        voucherCodeRef.current?.focus();
      }}>
            <form onSubmit={onApplyDiscount} aria-label={formatMessage({
          id: 'applyVoucherFormLabel',
          defaultMessage: 'Apply Voucher Code'
        })}>
              <InputText id="flyoutCartVoucherCode" name="voucherCode" type="text" placeholder={voucherPlaceholder} value={voucherValue.voucherCode} onChange={handleInputChange} ref={voucherCodeRef} hasError={hasVoucherError} required />

              {voucherValue.voucherCode.length >= 11 && <p id="voucherPinInstruction" className="sr-only">
                  {formatMessage({
              id: 'onlinePinRequired',
              defaultMessage: 'Bitte geben Sie den 4-stellingen ONLINE-PIN ein.'
            })}
                </p>}

              <div className={styles.voucherApplyWrapper}>
                <CustomMotion className={styles.pinWrapper} initial={{
              flexGrow: 0,
              marginRight: 0,
              overflow: 'hidden'
            }} animate={voucherValue.voucherCode.length >= 11 ? {
              flexGrow: 1,
              marginRight: '8px'
            } : {
              flexGrow: 0,
              marginRight: 0
            }} transition={{
              duration: 0.1,
              ease: 'linear'
            }}>
                  <InputText id="flyoutCartVoucherPin" name="voucherPin" type="text" placeholder={formatMessage({
                id: 'onlinePin',
                defaultMessage: 'Online-Pin'
              })} value={voucherValue.voucherPin} onChange={handleInputChange} hasError={hasVoucherError} describedBy="voucherPinInstruction" />
                </CustomMotion>

                <Button buttonType="submit" label={redeemVoucherBtnLabel} className={`cta ${styles.applyButton}`} hasIcon={false} onClick={onApplyDiscount} disabled={voucherValue.voucherCode === '' || voucherValue.voucherCode.length >= 11 && voucherValue.voucherPin === ''} />

                <div className={styles.voucherHint}>
                  <Transition show={showHint} enter="transition-transform duration-4" enterFrom="translate-y-full" enterTo="translate-y-0" leave="transition-transform duration-4" leaveFrom="translate-y-0" leaveTo="translate-y-full" afterLeave={closeCallback}>
                    <div id="voucherHint" role="alert" tabIndex={-1} ref={hintRef} aria-live="assertive" className={classNames(styles.hint, {
                  [styles.hasError]: hasVoucherError
                })}>
                      {hasVoucherError ? error ?? voucherInvalidLabel : voucherRedeemedLabel}
                    </div>
                  </Transition>
                </div>
              </div>

              <button type="button" className={classNames('primary', styles.primaryBtn)} onClick={closeVoucherInput}>
                {backBtnLabel}
              </button>
            </form>
          </CustomMotion> : <CustomMotion className={styles.flyoutCartButtons} key="buttonsSection" initial="visible" animate="visible" exit="exit" variants={slideFadeVariants} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} onAnimationComplete={() => {
        if (!isVoucherSectionOpen && shouldFocusVoucherBtn.current) {
          voucherBtnRef.current?.focus();
          shouldFocusVoucherBtn.current = false;
        }
      }}>
            {!isEmptyCart && <button className="cta" onClick={goToCheckout}>
                {toCheckoutBtnLabel}
              </button>}
            <button className={classNames('primary', styles.primaryBtn)} onClick={() => {
          setHasVoucherError(false);
          setIsVoucherSectionOpen(true);
        }} aria-expanded={isVoucherSectionOpen} aria-controls="voucher-section" ref={voucherBtnRef}>
              {voucherBtnLabel}
            </button>
            <button className={classNames('primary', styles.primaryBtn)} onClick={() => {
          router.push('/cart');
          updateGlobalState(false);
        }}>
              {toBasketBtnLabel}
            </button>
          </CustomMotion>}
      </AnimatePresence>
    </>;
};